/**
 * Whether the table has room to draw cards on a seat.
 *
 * Four lines around `NARROW_QUERY`; the rule itself is in `render/narrow.ts`
 * where it can be tested. Same split as `use-counter-event.ts`.
 *
 * `useSyncExternalStore` rather than an effect and a piece of state: the
 * viewport is external state that React did not change, and subscribing to it
 * properly is what stops the first paint disagreeing with the second.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { NARROW_QUERY } from '../render/narrow.js';

export function useNarrow(): boolean {
  const subscribe = useCallback((changed: () => void) => {
    if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
    const query = window.matchMedia(NARROW_QUERY);
    query.addEventListener('change', changed);
    return () => query.removeEventListener('change', changed);
  }, []);

  const read = useCallback(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(NARROW_QUERY).matches;
  }, []);

  /*
   * The server snapshot is "not narrow", which is what a static render gets.
   *
   * There is no viewport to measure without a window, and guessing narrow would
   * make every test that renders to markup draw a phone.
   */
  return useSyncExternalStore(subscribe, read, () => false);
}
